import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Le due password non coincidono.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen grid place-items-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-lg tracking-tight w-max mb-10">
          <img src="/lumora-logo.png" alt="Lumora" className="w-7 h-7" />
          LUMORA<span className="text-[#E05638]">.</span>
        </Link>

        {done ? (
          <div className="text-center">
            <CheckCircle2 size={48} className="text-emerald-500 mx-auto" />
            <h1 className="font-display font-bold text-3xl mt-6">Password aggiornata!</h1>
            <p className="text-neutral-600 text-sm mt-3">Ora puoi accedere con la nuova password.</p>
            <button
              data-testid="reset-goto-login"
              onClick={() => navigate("/accedi")}
              className="mt-8 rounded-full bg-[#111111] text-white px-7 py-3.5 text-sm font-semibold hover:bg-[#E05638] transition-colors"
            >
              Vai al login
            </button>
          </div>
        ) : (
          <>
            <h1 className="font-display font-bold text-3xl tracking-tight">Scegli una nuova password</h1>
            <p className="text-neutral-500 text-sm mt-2 mb-8">Minimo 8 caratteri.</p>
            {!token ? (
              <p className="text-sm text-[#C8452A] bg-[#E05638]/10 rounded-lg px-4 py-3">
                Link non valido. Richiedi un nuovo link dalla pagina di accesso.
              </p>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <input
                  data-testid="reset-password-input"
                  type="password"
                  required
                  minLength={8}
                  placeholder="Nuova password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-xl border border-black/15 bg-white px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900"
                />
                <input
                  data-testid="reset-password-confirm"
                  type="password"
                  required
                  minLength={8}
                  placeholder="Ripeti la nuova password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full rounded-xl border border-black/15 bg-white px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900"
                />
                {error && (
                  <p data-testid="reset-error" className="text-sm text-[#C8452A] bg-[#E05638]/10 rounded-lg px-4 py-3">
                    {error}
                  </p>
                )}
                <button
                  data-testid="reset-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#111111] text-white py-3.5 text-sm font-semibold hover:bg-[#E05638] transition-colors disabled:opacity-60"
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  Salva nuova password
                </button>
              </form>
            )}
            <Link
              to="/accedi"
              data-testid="reset-back-login"
              className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-[#111111] transition-colors mt-8"
            >
              <ArrowLeft size={15} /> Torna al login
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
